import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { notificar } from '@/shared/ui/avisos';

interface Cambio {
  campo: string;
  antes: string | null;
  despues: string;
}

type Veredicto = 'ALTA' | 'ACTUALIZACION' | 'SIN_CAMBIOS' | 'RECHAZADA';

interface Linea {
  fila: number;
  identificacion: string | null;
  nombre: string | null;
  numeroCuenta: string | null;
  veredicto: Veredicto;
  motivo: string;
  cambios: Cambio[];
  avisos: string[];
}

interface Resultado {
  clientesNuevos: number;
  clientesActualizados: number;
  cuentasNuevas: number;
  cuentasActualizadas: number;
  rechazadas: number;
  sinCambios: number;
}

interface Plan {
  lineas: Linea[];
  columnasIgnoradas: string[];
  altas: number;
  actualizaciones: number;
  sinCambios: number;
  rechazadas: number;
}

const VEREDICTOS: Record<Veredicto, { texto: string; tono: string; fila: string }> = {
  ALTA: { texto: 'Alta', tono: 'bg-marca-50 text-marca-700', fila: '' },
  ACTUALIZACION: { texto: 'Actualiza', tono: 'bg-amber-50 text-amber-700', fila: '' },
  SIN_CAMBIOS: { texto: 'Sin cambios', tono: 'bg-armazon-100 text-armazon-500', fila: 'opacity-60' },
  RECHAZADA: { texto: 'Rechazada', tono: 'bg-red-50 text-red-700', fila: 'bg-red-50/40' },
};

export function PaginaImportarCatastro() {
  const [fichero, setFichero] = useState<File | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [encima, setEncima] = useState(false);
  const [bajando, setBajando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const clienteDeConsultas = useQueryClient();

  const revisar = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      return api.subir<Plan>('/catastro/importacion/previsualizacion', cuerpo);
    },
    onSuccess: setPlan,
    onError: (causa) => {
      setPlan(null);
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo leer el fichero');
    },
  });

  const aplicar = useMutation({
    mutationFn: (archivo: File) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo);
      return api.subir<Resultado>('/catastro/importacion/aplicar', cuerpo);
    },
    onSuccess: (r) => {
      setResultado(r);
      setPlan(null);
      notificar.exito(
        'IMPORTADO: ' + r.clientesNuevos + ' CLIENTES NUEVOS Y ' + r.cuentasNuevas + ' CUENTAS NUEVAS',
      );

      void clienteDeConsultas.invalidateQueries({ queryKey: ['clientes'] });
      void clienteDeConsultas.invalidateQueries({ queryKey: ['cuentas'] });
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo aplicar'),
  });

  function elegir(archivo: File | null | undefined) {
    if (!archivo) {
      return;
    }
    setFichero(archivo);
    setPlan(null);
    setResultado(null);
    revisar.mutate(archivo);
  }

  async function descargarPlantilla() {
    setBajando(true);
    try {
      const excel = await api.descargar('/catastro/importacion/plantilla.xlsx');
      if (!excel) {
        notificar.error('NO SE PUDO ARMAR LA PLANTILLA');
        return;
      }
      const url = URL.createObjectURL(excel);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = 'plantilla-catastro.xlsx';
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      URL.revokeObjectURL(url);
    } catch (causa) {
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo descargar');
    } finally {
      setBajando(false);
    }
  }

  return (
    <>
      <EncabezadoDePagina
        icono={FileSpreadsheet}
        color="indigo"
        titulo="Importar catastro"
        subtitulo="Dar de alta clientes y cuentas desde una hoja de cálculo"
        acciones={
          <button
            type="button"
            onClick={() => void descargarPlantilla()}
            disabled={bajando}
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-armazon-200 bg-white px-4 text-[11px] font-bold uppercase tracking-wide text-armazon-600 transition hover:border-armazon-300 disabled:opacity-40"
          >
            {bajando ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Download className="size-4" aria-hidden />
            )}
            Plantilla
          </button>
        }
      />

      <div
        onDragOver={(evento) => {
          evento.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(evento) => {
          evento.preventDefault();
          setEncima(false);
          elegir(evento.dataTransfer.files?.[0]);
        }}
        className={`mb-4 rounded-2xl border-2 border-dashed p-8 text-center transition ${
          encima ? 'border-marca-400 bg-marca-50/50' : 'border-armazon-200 bg-white'
        }`}
      >
        <input
          ref={entrada}
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={(evento) => elegir(evento.target.files?.[0])}
        />

        <Upload className="mx-auto mb-3 size-8 text-armazon-300" aria-hidden />

        <p className="text-sm font-semibold uppercase tracking-wide text-armazon-600">
          {fichero ? fichero.name : 'Arrastre aquí el archivo .xlsx'}
        </p>

        <p className="mt-1 text-xs text-armazon-400">
          Sólo se mira: no se escribe nada hasta que usted lo confirme
        </p>

        <button
          type="button"
          onClick={() => entrada.current?.click()}
          disabled={revisar.isPending}
          className="mt-4 inline-flex h-10 items-center gap-2 rounded-xl bg-armazon-800 px-5 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-armazon-900 disabled:opacity-40"
        >
          {revisar.isPending ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              Leyendo
            </>
          ) : (
            <>Elegir archivo</>
          )}
        </button>
      </div>

      {resultado && <YaEsta resultado={resultado} />}

      {plan && (
        <Informe
          plan={plan}
          aplicando={aplicar.isPending}
          alConfirmar={() => fichero && aplicar.mutate(fichero)}
        />
      )}
    </>
  );
}

function Informe({
  plan,
  aplicando,
  alConfirmar,
}: {
  plan: Plan;
  aplicando: boolean;
  alConfirmar: () => void;
}) {
  return (
    <>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra rotulo="Altas" valor={plan.altas} tono="text-marca-700" />
        <Cifra rotulo="Actualizaciones" valor={plan.actualizaciones} tono="text-amber-700" />
        <Cifra rotulo="Sin cambios" valor={plan.sinCambios} tono="text-armazon-500" />
        <Cifra
          rotulo="Rechazadas"
          valor={plan.rechazadas}
          tono={plan.rechazadas > 0 ? 'text-red-700' : 'text-armazon-500'}
        />
      </div>

      {plan.columnasIgnoradas.length > 0 && (

        <p className="mb-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            Estas columnas del fichero no se van a usar:{' '}
            <strong>{plan.columnasIgnoradas.join(', ')}</strong>. Si alguna debería entrar,
            compruebe su nombre contra la plantilla.
          </span>
        </p>
      )}

      <div className="overflow-hidden rounded-2xl border border-armazon-100 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-armazon-50/80">
              <tr>
                {['Fila', 'Cliente', 'Cuenta', 'Qué pasa', 'Detalle'].map((c) => (
                  <th
                    key={c}
                    scope="col"
                    className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-armazon-500"
                  >
                    {c}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-armazon-100">
              {plan.lineas.map((l) => (
                <tr key={l.fila} className={VEREDICTOS[l.veredicto].fila}>
                  <td className="px-3 py-3 align-top">
                    <span className="font-mono text-xs font-semibold tabular-nums text-armazon-500">
                      {l.fila}
                    </span>
                  </td>

                  <td className="px-3 py-3 align-top">
                    <span className="block truncate text-xs font-medium text-armazon-700">
                      {l.nombre ?? '—'}
                    </span>
                    <span className="block font-mono text-[10px] text-armazon-400">
                      {l.identificacion ?? '—'}
                    </span>
                  </td>

                  <td className="px-3 py-3 align-top">
                    <span className="font-mono text-xs text-armazon-600">
                      {l.numeroCuenta ?? '—'}
                    </span>
                  </td>

                  <td className="px-3 py-3 align-top">
                    <span
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-1 text-[10px] font-bold uppercase ${VEREDICTOS[l.veredicto].tono}`}
                    >
                      {l.veredicto === 'RECHAZADA' ? (
                        <XCircle className="size-3" aria-hidden />
                      ) : (
                        <CheckCircle2 className="size-3" aria-hidden />
                      )}
                      {VEREDICTOS[l.veredicto].texto}
                    </span>
                  </td>

                  <td className="px-3 py-3 align-top">
                    <span
                      className={`block text-[11px] leading-relaxed ${
                        l.veredicto === 'RECHAZADA'
                          ? 'font-medium text-red-700'
                          : 'text-armazon-500'
                      }`}
                    >
                      {l.motivo}
                    </span>

                    {l.cambios.length > 0 && (
                      <span className="mt-1.5 flex flex-wrap gap-1.5">
                        {l.cambios.map((c) => (
                          <span
                            key={c.campo}
                            className="inline-flex items-center gap-1 rounded bg-armazon-100 px-1.5 py-0.5 text-[10px] text-armazon-600"
                          >
                            <span className="font-semibold">{c.campo}</span>
                            {c.antes && (
                              <>
                                <span className="text-armazon-400 line-through">{c.antes}</span>
                                <ArrowRight className="size-2.5 text-armazon-400" aria-hidden />
                              </>
                            )}
                            <span>{c.despues}</span>
                          </span>
                        ))}
                      </span>
                    )}

                    {l.avisos.map((aviso) => (
                      <span
                        key={aviso}
                        className="mt-1.5 flex items-start gap-1 text-[10px] leading-relaxed text-amber-700"
                      >
                        <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden />
                        {aviso}
                      </span>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-armazon-100 px-5 py-4">
          <p className="text-xs uppercase tracking-wide text-armazon-500">
            {plan.altas + plan.actualizaciones > 0
              ? `Se van a aplicar ${plan.altas} altas y ${plan.actualizaciones} actualizaciones.`
              : 'No hay nada que aplicar con este fichero.'}{' '}
            {plan.rechazadas > 0 && (
              <span className="font-semibold text-red-700">
                Las {plan.rechazadas} rechazadas se quedan fuera.
              </span>
            )}
          </p>

          <button
            type="button"
            onClick={alConfirmar}
            disabled={aplicando || plan.altas + plan.actualizaciones === 0}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-marca-600 px-5 text-[11px] font-bold uppercase tracking-wide text-white transition hover:bg-marca-700 disabled:opacity-40"
          >
            {aplicando ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Aplicando
              </>
            ) : (
              <>
                <CheckCircle2 className="size-4" aria-hidden />
                Confirmar e importar
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}

function YaEsta({ resultado }: { resultado: Resultado }) {
  return (
    <div className="mb-4 rounded-2xl border border-marca-200 bg-marca-50/60 p-5">
      <p className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-marca-800">
        <CheckCircle2 className="size-5" aria-hidden />
        Importación aplicada
      </p>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Cifra rotulo="Clientes nuevos" valor={resultado.clientesNuevos} tono="text-marca-700" />
        <Cifra
          rotulo="Clientes actualizados"
          valor={resultado.clientesActualizados}
          tono="text-armazon-700"
        />
        <Cifra rotulo="Cuentas nuevas" valor={resultado.cuentasNuevas} tono="text-marca-700" />
        <Cifra
          rotulo="Cuentas actualizadas"
          valor={resultado.cuentasActualizadas}
          tono="text-armazon-700"
        />
      </div>

      {resultado.rechazadas > 0 && (
        <p className="mt-3 flex items-start gap-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
          {resultado.rechazadas} filas quedaron fuera. Corríjalas en el fichero y vuelva a
          subirlo: las que ya entraron saldrán como «sin cambios».
        </p>
      )}
    </div>
  );
}

function Cifra({ rotulo, valor, tono }: { rotulo: string; valor: number; tono: string }) {
  return (
    <div className="rounded-2xl border border-armazon-200 bg-white px-4 py-3">
      <span className={`block text-xl font-bold tabular-nums leading-none ${tono}`}>{valor}</span>
      <span className="mt-1 block text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
        {rotulo}
      </span>
    </div>
  );
}
